import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { SceneManagerConfig } from './types';
import {
  CAMERA_INITIAL_POSITION,
  CAMERA_MIN_DISTANCE,
  CAMERA_MAX_DISTANCE,
  DELTA_TIME_CLAMP,
  BACKGROUND_COLOR,
  ORBIT_SEMI_MAJOR_AXIS,
  ORBIT_ECCENTRICITY_DEFAULT,
  ORBIT_SPEED_DEFAULT,
} from './constants';
import { NeutronStar } from './NeutronStar';
import { JetStreamSystem } from './JetStreamSystem';
import { AxisVisualizer } from './AxisVisualizer';
import { FieldLineRenderer } from './FieldLineRenderer';
import { LightingSetup } from './LightingSetup';
import { OrbitalSystem } from './OrbitalSystem';
import { OrbitRenderer } from './OrbitRenderer';
import { PerformanceScaler } from './PerformanceScaler';
import { JetStreamConfig, FieldLineConfig } from './types';

/**
 * SceneManager owns the Three.js renderer, scene, camera, controls, and animation loop.
 * It wires together NeutronStar, JetStreamSystem, AxisVisualizer, and LightingSetup.
 */
export class SceneManager {
  public renderer: THREE.WebGLRenderer;
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  public controls: OrbitControls;
  public clock: THREE.Clock;

  private neutronStar: NeutronStar;
  private jetSystem: JetStreamSystem;
  private axisVisualizer: AxisVisualizer;
  private fieldLineRenderer: FieldLineRenderer;
  private lightingSetup: LightingSetup;
  private orbitalSystem: OrbitalSystem;
  private orbitRenderer: OrbitRenderer;
  private animationFrameId: number | null = null;
  private container: HTMLElement;
  private neutronStarGroup: THREE.Group;
  private touchStartHandler: ((e: TouchEvent) => void) | null = null;
  private touchMoveHandler: ((e: TouchEvent) => void) | null = null;

  constructor(config: SceneManagerConfig) {
    this.container = config.container;

    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(BACKGROUND_COLOR);

    // Create perspective camera
    const aspect = this.container.clientWidth / this.container.clientHeight || 1;
    this.camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
    this.camera.position.copy(CAMERA_INITIAL_POSITION);
    this.camera.lookAt(0, 0, 0);

    // Create WebGL renderer with availability check
    try {
      const antialias = config.performanceConfig?.antialias ?? true;
      this.renderer = new THREE.WebGLRenderer({ antialias });
    } catch (error) {
      this.showWebGLError();
      throw new Error('WebGL is required to view this visualization. Please use a modern browser with WebGL support.');
    }

    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    const maxPixelRatio = config.performanceConfig?.maxPixelRatio ?? Infinity;
    const pixelRatio = PerformanceScaler.clampPixelRatio(window.devicePixelRatio, maxPixelRatio);
    this.renderer.setPixelRatio(pixelRatio);
    this.container.appendChild(this.renderer.domElement);

    // Prevent browser gesture conflicts with the canvas
    this.renderer.domElement.style.touchAction = 'none';

    // Set up OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.minDistance = CAMERA_MIN_DISTANCE;
    this.controls.maxDistance = CAMERA_MAX_DISTANCE;
    // Left drag = orbit (default), scroll = zoom (default), right drag = pan (default)
    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    };

    // Mobile touch optimization
    if (config.isMobile) {
      this.controls.dampingFactor = 0.1;
      this.controls.touches = {
        ONE: THREE.TOUCH.ROTATE,
        TWO: THREE.TOUCH.DOLLY_PAN,
      };
    }

    // Prevent default touch behaviors on mobile (scroll, pull-to-refresh)
    // Note: preventDefault() on the canvas does not interfere with OrbitControls because
    // OrbitControls registers its own listeners on the same element. Both fire independently.
    // addEventListener does not prevent propagation to other listeners on the same element.
    if (config.isMobile) {
      this.touchStartHandler = (e: TouchEvent) => e.preventDefault();
      this.touchMoveHandler = (e: TouchEvent) => e.preventDefault();
      this.renderer.domElement.addEventListener('touchstart', this.touchStartHandler, { passive: false });
      this.renderer.domElement.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    }

    // Create clock for delta time
    this.clock = new THREE.Clock();

    // Create NeutronStar with defaults, passing geometry options from performance config
    this.neutronStar = new NeutronStar(undefined, {
      widthSegments: config.performanceConfig?.sphereWidthSegments,
      heightSegments: config.performanceConfig?.sphereHeightSegments,
    });

    // Create JetStreamSystem with default config
    const defaultJetConfig: JetStreamConfig = {
      particleCount: 10000,
      speed: 100,
      magneticOffsetAngle: 18,
      accentColor: new THREE.Color('#98f0be'),
      coneHalfAngle: 6,
    };
    this.jetSystem = new JetStreamSystem(defaultJetConfig, this.neutronStar);

    // Create neutronStarGroup and add child objects to it
    this.neutronStarGroup = new THREE.Group();
    this.neutronStarGroup.add(this.neutronStar.mesh);
    this.neutronStarGroup.add(this.jetSystem.northJet.points);
    this.neutronStarGroup.add(this.jetSystem.southJet.points);

    // Create AxisVisualizer — adds its lines to the group
    this.axisVisualizer = new AxisVisualizer(this.neutronStarGroup);

    // Create FieldLineRenderer — adds field line curves to the star mesh
    // so they co-rotate with the star's spin (aligned to magnetic axis)
    this.fieldLineRenderer = new FieldLineRenderer(this.neutronStar.mesh, undefined, {
      segmentBudget: config.performanceConfig?.fieldLineSegmentBudget,
      maxSegmentsPerCurve: config.performanceConfig?.fieldLineSegmentsPerCurve,
    });

    // Add the group to the scene
    this.scene.add(this.neutronStarGroup);

    // Instantiate OrbitalSystem with default params
    this.orbitalSystem = new OrbitalSystem();

    // Instantiate OrbitRenderer with default params and add line to scene
    this.orbitRenderer = new OrbitRenderer(
      {
        semiMajorAxis: ORBIT_SEMI_MAJOR_AXIS,
        eccentricity: ORBIT_ECCENTRICITY_DEFAULT,
        orbitalSpeed: ORBIT_SPEED_DEFAULT,
      },
      config.performanceConfig?.orbitLineSegments
    );
    this.scene.add(this.orbitRenderer.getLine());

    // Create LightingSetup
    this.lightingSetup = new LightingSetup(this.scene);

    // Wire resize handler
    window.addEventListener('resize', this.onResize.bind(this));
  }

  /**
   * Starts the animation loop.
   */
  start(): void {
    if (this.animationFrameId !== null) return;
    this.clock.start();
    this.animate();
  }

  /**
   * Stops the animation loop.
   */
  stop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.clock.stop();
  }

  /**
   * Cleans up event listeners and resources.
   */
  dispose(): void {
    this.stop();
    window.removeEventListener('resize', this.onResize.bind(this));

    if (this.touchStartHandler) {
      this.renderer.domElement.removeEventListener('touchstart', this.touchStartHandler);
      this.touchStartHandler = null;
    }
    if (this.touchMoveHandler) {
      this.renderer.domElement.removeEventListener('touchmove', this.touchMoveHandler);
      this.touchMoveHandler = null;
    }

    this.controls.dispose();
  }

  /**
   * Handles window resize: updates renderer size and camera aspect ratio.
   */
  onResize(): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    // Skip resize if dimensions are zero
    if (width === 0 || height === 0) return;

    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Clamps a raw delta time to the maximum allowed value.
   * Exposed for testing.
   */
  clampDelta(rawDelta: number): number {
    return Math.min(rawDelta, DELTA_TIME_CLAMP);
  }

  // --- Getter methods for testing ---

  getNeutronStar(): NeutronStar {
    return this.neutronStar;
  }

  getJetSystem(): JetStreamSystem {
    return this.jetSystem;
  }

  getAxisVisualizer(): AxisVisualizer {
    return this.axisVisualizer;
  }

  getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  getControls(): OrbitControls {
    return this.controls;
  }

  getNeutronStarGroup(): THREE.Group {
    return this.neutronStarGroup;
  }

  getOrbitalSystem(): OrbitalSystem {
    return this.orbitalSystem;
  }

  getOrbitRenderer(): OrbitRenderer {
    return this.orbitRenderer;
  }

  getFieldLineRenderer(): FieldLineRenderer {
    return this.fieldLineRenderer;
  }

  /**
   * Updates orbital parameters from external sources (e.g., ControlPanel).
   */
  updateOrbitalParams(params: { orbitalSpeed?: number; eccentricity?: number; semiMajorAxis?: number }): void {
    if (params.orbitalSpeed !== undefined) {
      this.orbitalSystem.setOrbitalSpeed(params.orbitalSpeed);
    }
    if (params.semiMajorAxis !== undefined) {
      this.orbitalSystem.setSemiMajorAxis(params.semiMajorAxis);
      this.orbitRenderer.updatePath(this.orbitalSystem.getParams());
    }
    if (params.eccentricity !== undefined) {
      this.orbitalSystem.setEccentricity(params.eccentricity);
      this.orbitRenderer.updatePath(this.orbitalSystem.getParams());
    }
  }

  /**
   * Updates field line parameters from external sources (e.g., ControlPanel).
   * Delegates to FieldLineRenderer.updateParams().
   */
  updateFieldLineParams(config: Partial<FieldLineConfig>): void {
    this.fieldLineRenderer.updateParams(config);
  }

  /**
   * Core animation loop. Each frame:
   * 1. Computes deltaTime via Clock.getDelta(), clamped to DELTA_TIME_CLAMP
   * 2. Updates NeutronStar rotation
   * 3. Updates JetStreamSystem particles
   * 4. Updates AxisVisualizer with current axis directions
   * 5. Updates OrbitControls
   * 6. Renders the scene
   */
  private animate(): void {
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));

    // Compute clamped delta time
    const rawDelta = this.clock.getDelta();
    const deltaTime = this.clampDelta(rawDelta);

    // Skip frame if delta is invalid
    if (isNaN(deltaTime) || deltaTime < 0) return;

    // Update neutron star rotation
    this.neutronStar.update(deltaTime);

    // Update jet stream particles
    this.jetSystem.update(deltaTime);

    // Update orbital position and move neutron star group
    const orbitalPos = this.orbitalSystem.update(deltaTime);
    this.neutronStarGroup.position.copy(orbitalPos);

    // Update axis visualizer with current directions
    this.axisVisualizer.updateSpinAxis(this.neutronStar.spinAxis);

    // Compute magnetic axis direction in world space for the visualizer
    const magneticAxisLocal = this.jetSystem.getMagneticAxisLocal();
    const magneticAxisWorld = magneticAxisLocal.clone()
      .applyQuaternion(this.neutronStar.mesh.quaternion)
      .normalize();
    this.axisVisualizer.updateMagneticAxis(magneticAxisWorld);

    // Update field line renderer with the current magnetic axis local direction
    this.fieldLineRenderer.updateMagneticAxis(magneticAxisLocal);

    // Update orbit controls
    this.controls.update();

    // Render the scene
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Displays a WebGL error message in the container when WebGL is unavailable.
   */
  private showWebGLError(): void {
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      background-color: #0a0a0f;
      color: #ffffff;
      font-family: sans-serif;
      font-size: 1.2rem;
      text-align: center;
      padding: 2rem;
    `;
    errorDiv.textContent = 'WebGL is required to view this visualization. Please use a modern browser with WebGL support.';
    this.container.appendChild(errorDiv);
  }
}
